// src/utils/localstorage.ts


// src/utils/localstorage.ts

// ---------------------------
// Auth Keys
// ---------------------------
export const LS_AUTH_KEY = "auth";

// ---------------------------
// Types
// ---------------------------
export type AuthData = {
  token: string;
  user: {
    id: number;
    name: string;
    email: string;
    // add more fields if needed
  };
};

// Save auth info
export function saveAuth(auth: AuthData) {
  localStorage.setItem(LS_AUTH_KEY, JSON.stringify(auth));
}

// Load auth info
export function loadAuth(): AuthData | null {
  const raw = localStorage.getItem(LS_AUTH_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as AuthData;
  } catch {
    return null;
  }
}

// Remove auth info (logout)
export function clearAuth() {
  localStorage.removeItem(LS_AUTH_KEY);
}

// Get token only
export function getToken(): string | null {
  const auth = loadAuth();
  return auth?.token || null;
}

// ---------------------------
// Types
// ---------------------------
export type GuideBlock = {
  id: string;
  title: string;
  category: "Accountant" | "Finance";
  level: "Beginner" | "Advanced";
  roleOverview: string;
  clientAcquisition: string[];
  pricingPackages: string[];
  toolsTemplates: string[];
  serviceStandards: string[];
  quickWins: string[];
  aiAutomationTips: string[];
};

// ---------------------------
// LocalStorage Key
// ---------------------------
export const LS_KEY = "smartGuides";

// ---------------------------
// Seed Data
// ---------------------------
export const seedGuides: GuideBlock[] = [
  {
    id: "accountant-beginner",
    title: "SmartGuide™ – Beginner Accountant",
    category: "Accountant",
    level: "Beginner",
    roleOverview: "Intro to bookkeeping, basic accounts payable/receivable, bank reconciliation, and financial record-keeping.",
    clientAcquisition: [
      "Target LinkedIn, Workason Accounting, and small business communities.",
      "Send 5–10 personalised proposals daily to SMEs needing bookkeeping help.",
      "Join 3 Facebook/LinkedIn groups for accounting and finance professionals.",
    ],
    pricingPackages: [
      "Start at £10–£15/hour to build a portfolio.",
      "Offer fixed monthly packages (basic bookkeeping, 10 hours/month).",
    ],
    toolsTemplates: ["QuickBooks, Xero, Google Sheets.", "Starter templates: invoice tracker, expense log, bank reconciliation sheet."],
    serviceStandards: ["Respond within 24 hours.", "Deliver monthly reports by the 5th of the following month."],
    quickWins: [
      "Create a SmartCV highlighting accounting certifications.",
      "Offer the first client a discounted month in exchange for a testimonial.",
      "Use AI tools to speed up data entry and report drafting.",
    ],
    aiAutomationTips: ["Use AI to categorise transactions automatically.", "Automate invoice reminders with accounting software workflows."],
  },
  {
    id: "accountant-advanced",
    title: "SmartGuide™ – Advanced Accountant",
    category: "Accountant",
    level: "Advanced",
    roleOverview: "Experienced accountant handling complex tasks: financial reporting, payroll management, tax preparation, and audit support.",
    clientAcquisition: [
      "Target high-value clients on LinkedIn via cold outreach and premium platforms.",
      "Leverage referrals and testimonials from past accounting work.",
    ],
    pricingPackages: ["£25–£50/hour.", "Offer premium retainers (full monthly accounting support packages)."],
    toolsTemplates: [
      "Sage, QuickBooks, Xero, and advanced Excel modelling.",
      "Templates: client onboarding forms, financial statement templates, audit preparation checklists.",
    ],
    serviceStandards: ["4–6 hour response time for client queries.", "Deliver all reports ahead of agreed deadlines."],
    quickWins: ["Run a LinkedIn series sharing accounting tips for SMEs.", "Create a portfolio page showcasing financial outcomes for past clients."],
    aiAutomationTips: ["Use AI for summarising financial reports.", "Automate recurring payroll workflows with payroll software integrations."],
  },
  {
    id: "finance-beginner",
    title: "SmartGuide™ – Beginner Finance Analyst",
    category: "Finance",
    level: "Beginner",
    roleOverview: "Introduction to financial analysis, budgeting basics, data interpretation, and financial modelling for small businesses.",
    clientAcquisition: [
      "Target LinkedIn, Workason Accounting, and startup communities.",
      "Offer a free sample financial snapshot to attract first clients.",
    ],
    pricingPackages: ["£8–£12/hour.", "Bundle: basic financial model + interpretation report."],
    toolsTemplates: ["Excel, Google Sheets, Power BI basics.", "Budget template, cash flow forecast sheet."],
    serviceStandards: ["24–48 hour turnaround for basic analysis tasks.", "One free revision included per deliverable."],
    quickWins: [
      "Build sample financial models using publicly available datasets.",
      "Join finance and investment communities for potential clients.",
    ],
    aiAutomationTips: ["Use AI to generate initial financial summaries.", "Automate data collection with Excel macros or Google Apps Script."],
  },
  {
    id: "finance-advanced",
    title: "SmartGuide™ – Advanced Finance Analyst",
    category: "Finance",
    level: "Advanced",
    roleOverview: "An established finance professional on Workason Accounting, aiming to scale, increase rates, and win premium clients needing forecasting, valuation, and strategic analysis.",
    clientAcquisition: ["Leverage AI Matching for high-value finance leads.", "Pitch to repeat clients for long-term financial advisory contracts."],
    pricingPackages: [
      "Increase rates by 20–50% based on portfolio strength.",
      "Offer VIP packages (priority delivery, scenario analysis, monthly retainers).",
    ],
    toolsTemplates: ["CRM integration to track clients.", "Personalised proposal templates with financial impact summaries."],
    serviceStandards: ["2–4 hour response time.", "Deliver analysis 1–2 days ahead of deadline."],
    quickWins: [],
    aiAutomationTips: [],
  },
];

// ---------------------------
// Helpers
// ---------------------------
export function loadGuides(): GuideBlock[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as GuideBlock[];
  } catch {
    return [];
  }
}

export function saveGuides(guides: GuideBlock[]): void {
  localStorage.setItem(LS_KEY, JSON.stringify(guides));
}

export function seedGuidesIfEmpty(): void {
  const existing = loadGuides();
  if (existing.length === 0) {
    saveGuides(seedGuides);
  }
}
